import { JobStatusDetail } from '../../../helpers/constants'
import { getOutstandingCashAdvancesOnJob, getUserFullName } from '../../../helpers/utils'

export const JobTransitions = {
  rejectDelivery: {
    transitionString: 'Reject Delivery',
    transitionAction: 'reject_delivery',
    confirmationMessage: (job: any) => {
      const driver = getUserFullName(job.assigned_to)
      return `This will reject ${driver}'s proof of delivery.`
    },
  },
  verifyDelivery: {
    transitionString: 'Verify Delivery',
    transitionAction: 'verify_delivery',
    confirmationMessage: (job: any) => {
      const team = job.team.name
      const driver = getUserFullName(job.assigned_to)
      const invoices = job.invoices
      let message = `This will verify ${driver}'s delivery for team: ${team}. `
      if (invoices.length === 0) {
        message += "You will need to generate an invoice, 'open' (finalize) it, and send it to the broker."
      }
      return message
    },
  },
  issuePayment: {
    transitionString: 'Issue Payment',
    transitionAction: 'issue_payment',
    confirmationMessage: (job: any) => {
      const team = job.team.name
      return `This will pay out the statement balance to ${team}.`
    },
  },
  pendingCancellation: {
    transitionString: 'Pending Cancellation',
    transitionAction: 'pend_cancellation',
    confirmationMessage: (job: any) => {
      const team = job.team.name
      const driver = getUserFullName(job.assigned_to)
      return `Job assigned to ${driver} of team: ${team} will be pending cancellation.`
    },
  },
  undoPendingCancellation: {
    transitionString: 'Undo Pending Cancellation',
    transitionAction: 'undo_pending_cancellation',
    confirmationMessage: (job: any) => {
      const team = job.team.name
      const driver = getUserFullName(job.assigned_to)
      return `Job assigned to ${driver} of team: ${team} will be moved out of pending cancellation to the previous state.`
    },
  },
  requestCancellation: {
    transitionString: 'Request Cancellation',
    transitionAction: 'request_cancellation',
    confirmationMessage: () => {},
  },
  returnToInProgress: {
    transitionString: 'Return To In Progress',
    transitionAction: 'undo_requested_cancellation',
    confirmationMessage: (job: any) => {
      const team = job.team.name
      const driver = getUserFullName(job.assigned_to)
      return `Job assigned to ${driver} of team: ${team} will be moved back to in progress.`
    },
  },
  cancel: {
    transitionString: 'Cancel',
    transitionAction: 'cancel',
    confirmationMessage: (job: any) => {
      const team = job.team.name
      const driver = getUserFullName(job.assigned_to)
      return `Job assigned to ${driver} of team: ${team} will be canceled. Please select the reason of cancelation.`
    },
    transitionTitle: (job: any) => {
      const cash_advances = getOutstandingCashAdvancesOnJob(job)
      if (cash_advances.length) {
        return 'Cancel job with active cash advance?'
      }
      return 'Cancel'
    },
  },
  deliver: {
    transitionString: 'Deliver',
    transitionAction: 'deliver',
    confirmationMessage: (job: any) => {
      const team = job.team.name
      const driver = getUserFullName(job.assigned_to)
      return `Job assigned to ${driver} of team: ${team} will be marked as delivered.`
    },
  },
  undeliver: {
    transitionString: 'Undeliver',
    transitionAction: 'undeliver',
    confirmationMessage: (job: any) => {
      const team = job.team.name
      const driver = getUserFullName(job.assigned_to)
      return `Job assigned to ${driver} on ${team} team will be moved back into In Progress.`
    },
  },
  approvePayment: {
    transitionString: 'Approve Payment',
    transitionAction: 'approve_payment',
    confirmationMessage: (job: any) => {
      const team = job.team.name
      const driver = getUserFullName(job.assigned_to)
      return `Job assigned to ${driver} on ${team} team will be approved for payment.`
    },
  },
  abort: {
    transitionString: 'Abort',
    transitionAction: 'abort',
    confirmationMessage: (job: any) => {
      const team = job.team.name
      const driver = getUserFullName(job.assigned_to)
      return `Job assigned to ${driver} of team: ${team} will be aborted.`
    },
  },
  awaitRateConfirmation: {
    transitionString: 'Await Rate Con',
    transitionAction: 'await_rate_confirmation',
    confirmationMessage: (job: any) => {
      const team = job.team.name
      const driver = getUserFullName(job.assigned_to)
      return `Job assigned to ${driver} of ${team} team will be moved into Awaiting Rate Confirmation.`
    },
  },
  receiveRateConfirmation: {
    transitionString: 'Receive Rate Con',
    transitionAction: 'receive_rate_confirmation',
    confirmationMessage: (job: any) => {
      const team = job.team.name
      const driver = getUserFullName(job.assigned_to)
      return `This will confirm the load for ${driver} of team ${team}.`
    },
  },
  bid: {
    transitionString: 'Bid',
    transitionAction: 'bid',
    confirmationMessage: () => {
      return 'By recording the placed bid, the job status will move to Active Negotiation.'
    },
  },
  start: {
    transitionString: 'Start',
    transitionAction: 'start',
    confirmationMessage: () => {
      return 'This load will be moved to In Progress (this automatically happens the night before the pickup date). Are you sure?'
    },
  },
  recover: {
    transitionString: 'Recovery needed',
    transitionAction: 'flag_for_recovery',
    confirmationMessage: () => {
      return 'A load will be created to be used in the rest of the recovery process.'
    },
  },
  recovered: {
    transitionString: 'Recovered',
    transitionAction: 'recovered',
    confirmationMessage: () => {
      return 'The recovery process is complete.'
    },
  },
}

export const JobFSMTransitions = {
  [JobStatusDetail.BiddingRequired]: [
    JobTransitions.bid,
    JobTransitions.receiveRateConfirmation,
    JobTransitions.abort,
  ],
  [JobStatusDetail.ActiveNegotiation]: [JobTransitions.receiveRateConfirmation, JobTransitions.abort],
  [JobStatusDetail.BookedOffline]: [JobTransitions.receiveRateConfirmation, JobTransitions.abort],
  [JobStatusDetail.BrokerSetupRequired]: [
    JobTransitions.awaitRateConfirmation,
    JobTransitions.receiveRateConfirmation,
    JobTransitions.abort,
  ],
  [JobStatusDetail.AwaitingRateConfirmation]: [JobTransitions.receiveRateConfirmation, JobTransitions.abort],
  [JobStatusDetail.ReviewingRateConfirmation]: [JobTransitions.pendingCancellation, JobTransitions.cancel],
  [JobStatusDetail.JobApproved]: [
    JobTransitions.start,
    JobTransitions.pendingCancellation,
    JobTransitions.cancel,
  ],
  [JobStatusDetail.InProgress]: [
    JobTransitions.deliver,
    JobTransitions.pendingCancellation,
    JobTransitions.recover,
    JobTransitions.cancel,
  ],
  [JobStatusDetail.PendingDeliveryVerification]: [
    JobTransitions.verifyDelivery,
    JobTransitions.rejectDelivery,
    JobTransitions.pendingCancellation,
    JobTransitions.cancel,
    JobTransitions.undeliver,
  ],
  [JobStatusDetail.PaymentRequired]: [
    JobTransitions.issuePayment,
    JobTransitions.pendingCancellation,
    JobTransitions.cancel,
    JobTransitions.undeliver,
  ],
  [JobStatusDetail.DeliveryVerificationRejected]: [
    JobTransitions.deliver,
    JobTransitions.cancel,
    JobTransitions.undeliver,
  ],
  [JobStatusDetail.PendingPaymentVerification]: [
    JobTransitions.approvePayment,
    JobTransitions.pendingCancellation,
    JobTransitions.cancel,
    JobTransitions.undeliver,
  ],
  [JobStatusDetail.RequestedCancellation]: [
    JobTransitions.returnToInProgress,
    JobTransitions.pendingCancellation,
    JobTransitions.cancel,
  ],
  [JobStatusDetail.PendingCancellation]: [JobTransitions.undoPendingCancellation, JobTransitions.cancel],
  [JobStatusDetail.RecoveryNeeded]: [JobTransitions.recovered],
  [JobStatusDetail.Recovered]: [JobTransitions.cancel, JobTransitions.issuePayment],
}

export const getJobTransitionProps = (
  job: any,
  includeCancel: boolean,
  allowLoadRecoveryAutomatedTransitions: boolean
) => {
  // TODO: find a cleaner way to ensure job passed in has required values for showing confirmation messages
  if (!job.assigned_to || !job.team || !job.invoices) {
    return null
  }
  const statusDetail = job.status_detail
  if (!Object.keys(JobFSMTransitions).includes(statusDetail)) {
    if (includeCancel && job.status !== 'PAST') {
      return [JobTransitions.cancel]
    }
    return null
  }
  const transitions = JobFSMTransitions[statusDetail]
  if (!includeCancel) {
    return transitions.filter(
      (transition) => transition.transitionAction !== JobTransitions.cancel.transitionAction
    )
  }

  if (!allowLoadRecoveryAutomatedTransitions && statusDetail === JobStatusDetail.InProgress) {
    return transitions.filter(
      (transition) => transition.transitionAction !== JobTransitions.recover.transitionAction
    )
  }
  if (!allowLoadRecoveryAutomatedTransitions) {
    return transitions.filter(
      (transition) =>
        transition.transitionAction !== JobTransitions.recover.transitionAction &&
        transition.transitionAction !== JobTransitions.recovered.transitionAction
    )
  }

  return transitions
}

export const JobAbortReason: Record<string, any> = {
  BROKER_UNRESPONSIVE: { id: 'BROKER_UNRESPONSIVE', name: 'The broker has not responded' },
  BECAME_UNAVAILBLE: { id: 'BECAME_UNAVAILBLE', name: 'Load became unavailable before bidding' },
  BID_REJECTED: { id: 'BID_REJECTED', name: 'Bid was rejected' },
  DRIVER_NOT_INTERESTED: { id: 'DRIVER_NOT_INTERESTED', name: 'Driver is no longer interested' },
  CT_IS_INELIGIBLE: { id: 'CT_IS_INELIGIBLE', name: 'CT is not eligible to move this load' },
  BOOKED_ANOTHER_LOAD: { id: 'BOOKED_ANOTHER_LOAD', name: 'Booked another load instead of this load' },
  BROKER_INELIGIBLE: { id: 'BROKER_INELIGIBLE', name: 'The Broker has been suspended or disqualified' },
  OTHER: { id: 'OTHER', name: 'Other (mis-created loads, errors, and etc.' },
}
